import React, { useState } from "react";
import RAPackingListInput from "./RAPackingListInput";
import ApiClient from "../../utils/ApiClient";

export default function RAShipmentScan({
                                           scanData = [],
                                           setScannedData,
                                           packingListData = [],
                                           packingListId,
                                           poNumber,
                                           setPackingListData,
                                           onScannedDataUpdate
                                       }) {
    const [updating, setUpdating] = useState(null);

    const handleQuantityUpdate = async (item, newQty) => {
        if (!item?.scanned_id || !packingListId || !poNumber) {
            console.error('Missing required data for update');
            const errorTone = new Audio('/sounds/error-tone.mp3');
            errorTone.play();
            return;
        }

        try {
            setUpdating(item.scanned_id);

            const response = await ApiClient.post("/api/receiveinventory/updatescan", {
                scannedId: item.scanned_id,
                packingListId,
                poNumber,
                receivedQty: newQty
            });

            if (response?.data) {
                setScannedData(response.data[0]?.scanhistory || []);
                setPackingListData(response.data[1]?.packingListData || []);
                const successTone = new Audio('/sounds/success-tone.mp3');
                successTone.play();

                if (onScannedDataUpdate) {
                    await onScannedDataUpdate();
                }
            }
        } catch (error) {
            console.error('Failed to update quantity:', error);
            alert('Failed to update quantity. Please try again.');
            const errorTone = new Audio('/sounds/error-tone.mp3');
            errorTone.play();
        } finally {
            setUpdating(null);
        }
    };

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            <RAPackingListInput
                packingListId={packingListId}
                packingListData={packingListData}
                poNumber={poNumber}
                onScannedDataUpdate={onScannedDataUpdate}
            />

            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full table-fixed divide-y divide-gray-300">
                            <thead>
                            <tr>
                                <th className="py-3.5 pl-4 pr-3 text-center text-sm font-semibold text-gray-900 sm:pl-3 w-1">
                                    Scanned Code
                                </th>
                                <th className="px-3 py-3.5 text-center text-sm text-gray-900 w-1 whitespace-normal break-words">
                                    Scan/Update Time
                                </th>
                                <th className="px-3 py-3.5 text-center text-sm text-gray-900 w-1 whitespace-normal break-words">
                                    Supplier Sku
                                </th>
                                <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                    Product Name
                                </th>
                                <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900 w-12">
                                    Qty Received
                                </th>
                                <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                    Running Qty Remaining
                                </th>
                                <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                    Running Total Received
                                </th>
                                <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                    Packing List Total Expected
                                </th>

                            </tr>
                            </thead>
                            <tbody className="bg-white">
                            {!Array.isArray(scanData) || scanData.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center text-gray-500 py-4">
                                        No scanned data available
                                    </td>
                                </tr>
                            ) : (
                                (() => {
                                    const firstOccurrences = new Set();
                                    const scannedCodesWithZeroRemaining = new Set();

                                    // First pass: identify scanned codes with zero remaining quantity
                                    scanData.forEach(item => {
                                        if (item && item.running_qty_remaining === 0) {
                                            scannedCodesWithZeroRemaining.add(item.scanned_id);
                                        }
                                    });

                                    return scanData.map((item, index) => {
                                        if (!item) return null;

                                        const isFirstOccurrence = !firstOccurrences.has(item.scanned_id);
                                        if (isFirstOccurrence) {
                                            firstOccurrences.add(item.scanned_id);
                                        }

                                        return (
                                            <tr
                                                key={`${item.calibrate_supplier_po_id}-${index}`}
                                                className={`${
                                                    scannedCodesWithZeroRemaining.has(item.scanned_id)
                                                        ? "bg-green-100"
                                                        : item.running_qty_remaining < 1
                                                            ? "bg-red-100"
                                                            : index % 2 === 0
                                                                ? "bg-gray-50"
                                                                : ""
                                                }`}
                                            >
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {item.scanned_id}
                                                </td>
                                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm sm:pr-3">
                                                    {item.updated_at}
                                                </td>
                                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-center text-sm sm:pr-3">
                                                    {item.supplier_sku}
                                                </td>
                                                <td className="px-3 py-4 text-sm text-gray-500 relative group">
                                                    <div className="truncate max-w-[200px] cursor-default">
                                                        {item.product_name}
                                                    </div>
                                                    <div
                                                        className="absolute left-0 -top-2 hidden group-hover:block z-50">
                                                        <div
                                                            className="bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg max-w-sm">
                                                            <p className="text-sm break-words whitespace-normal">
                                                                {item.product_name}
                                                            </p>
                                                            <div
                                                                className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-[50px]">
                                                    {isFirstOccurrence ? (
                                                        <div className="relative">
                                                            <input
                                                                type="number"
                                                                defaultValue={item.received_qty}
                                                                disabled={updating === item.scanned_id}
                                                                className={`w-[75px] whitespace-nowrap px-3 py-4 text-sm text-gray-500 border border-gray-300 rounded
                                                                        ${updating === item.scanned_id ? 'bg-gray-100' : ''}
                                                                        focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                                                                onBlur={(e) => {
                                                                    const newQty = parseInt(e.target.value);
                                                                    if (!isNaN(newQty) && newQty !== item.received_qty) {
                                                                        handleQuantityUpdate(item, newQty);
                                                                    }
                                                                }}
                                                                onKeyPress={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.target.blur();
                                                                    }
                                                                }}
                                                            />
                                                            {updating === item.scanned_id && (
                                                                <div
                                                                    className="absolute right-0 top-0 h-full flex items-center pr-2">
                                                                    <div
                                                                        className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        item.received_qty
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {item.running_qty_remaining}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {item.running_total_received}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {item.packinglist_qty}
                                                </td>

                                            </tr>
                                        );
                                    }).filter(Boolean)
                                })()
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}